/**
 * The app's map, as Brian's prototype draws it (desktop-nav.js NAV, topbar.js
 * ROOTS and back targets, DEVELOPER-HANDOFF §4 Navigation).
 *
 * One list feeds the desktop sidebar, the desktop toolbar's "Group › Screen"
 * and the phone's root titles, so a screen is named the same way everywhere.
 * Route paths live in ROUTES; docs/PORT-ROUTES.md lists them with their files.
 */

export const ROUTES = {
  feed: '/app',
  feedControls: '/app/feed-controls',
  topics: '/app/feed/topics',
  places: '/app/feed/places',
  breaking: '/app/feed/breaking',
  recordsSettings: '/app/feed/records',
  library: '/app/library',
  record: '/app/record',
  recordPick: '/app/record/pick',
  alerts: '/app/alerts',
  alert: (id: string) => `/app/alerts/${id}`,
  records: '/app/records',
  recordFind: (id: string) => `/app/records/${id}`,
  story: (id: string) => `/app/story/${id}`,
  settings: '/app/settings',
  profile: '/app/profile',
  achievements: '/app/achievements',
  newsletter: '/app/newsletter',
  newsletterSponsors: '/app/newsletter/sponsors',
  newsletterBrand: '/app/newsletter/brand',
  newsletterConnect: '/app/newsletter/connect',
  affiliate: '/app/affiliate',
  soon: (what: string) => `/app/soon/${what}`,
} as const

/** Every sidebar item has a key; a screen names its key in <PageChrome navKey>. */
export type NavKey =
  | 'feed' | 'library' | 'alerts' | 'records'
  | 'newsletter' | 'newsletter.issue' | 'newsletter.past' | 'newsletter.sponsors' | 'newsletter.brand' | 'newsletter.list'
  | 'topics' | 'places' | 'breaking' | 'records-settings'
  | 'settings' | 'achievements'
  | 'seeds' | 'affiliate'

export interface NavItem {
  key: NavKey
  label: string
  to: string
  /** Not built yet: listed with "soon" and opens the coming-soon page. */
  soon?: boolean
  subs?: NavItem[]
}

export interface NavGroup {
  label: string
  items: NavItem[]
  /** Not on the desktop sidebar (Brian's four groups); reached from Settings. */
  deskHidden?: boolean
}

export const NAV: NavGroup[] = [
  { label: 'Create', items: [
    { key: 'feed', label: 'Your feed', to: ROUTES.feed },
    { key: 'library', label: 'Library', to: ROUTES.library },
    { key: 'alerts', label: 'Alerts', to: ROUTES.alerts },
    { key: 'records', label: 'Record finds', to: ROUTES.records },
  ] },
  { label: 'Publish', items: [
    { key: 'newsletter', label: 'Newsletter', to: ROUTES.newsletter, subs: [
      { key: 'newsletter.issue', label: 'This week’s issue', to: ROUTES.newsletter },
      { key: 'newsletter.past', label: 'Past issues', to: ROUTES.soon('past-issues'), soon: true },
      { key: 'newsletter.sponsors', label: 'Sponsors', to: ROUTES.newsletterSponsors },
      { key: 'newsletter.brand', label: 'Brand studio', to: ROUTES.newsletterBrand },
      { key: 'newsletter.list', label: 'Your list', to: ROUTES.newsletterConnect },   // Oct 29 (Brian): GoGrov sends; no Mailchimp for now
    ] },
  ] },
  { label: 'Feed controls', items: [
    { key: 'topics', label: 'Topics', to: ROUTES.topics },
    { key: 'places', label: 'Places', to: ROUTES.places },
    { key: 'breaking', label: 'Breaking news', to: ROUTES.breaking },
    { key: 'records-settings', label: 'Public records', to: ROUTES.recordsSettings },
  ] },
  { label: 'Account', items: [
    { key: 'settings', label: 'Settings', to: ROUTES.settings },
    { key: 'achievements', label: 'Achievements', to: ROUTES.achievements },
  ] },
  { label: 'More', deskHidden: true, items: [
    { key: 'seeds', label: 'Seed bank', to: ROUTES.soon('seeds'), soon: true },
    { key: 'affiliate', label: 'Affiliate', to: ROUTES.affiliate },
  ] },
]

/** The phone's root tabs: they show a large title and no Back. */
export const ROOT_TABS: Partial<Record<NavKey, { title: string; back: string; to: string }>> = {
  feed: { title: 'Your feed', back: 'Feed', to: ROUTES.feed },
  library: { title: 'Library', back: 'Library', to: ROUTES.library },
  alerts: { title: 'Alerts', back: 'Alerts', to: ROUTES.alerts },
}

/** "Group › Screen" for the desktop toolbar (desktop-nav.js adapt()). */
export function crumbFor(navKey: NavKey | undefined, title: string): { group: string | null; name: string } {
  if (!navKey) return { group: null, name: title }
  for (const g of NAV) {
    for (const it of g.items) {
      if (it.key === navKey) {
        // A screen inside an item (a story under Your feed, one alert) is
        // named under that item: "Your feed › Story".
        return it.label === title || !title ? { group: g.label, name: it.label } : { group: it.label, name: title }
      }
      for (const s of it.subs ?? []) {
        if (s.key === navKey) return s.label === title || !title ? { group: it.label, name: s.label } : { group: s.label, name: title }
      }
    }
  }
  return { group: null, name: title }
}
